import type { Language } from "./types"

// Colours for the code the docs show: request examples, JSON responses and shell commands. It
// is a handful of patterns for those shapes and not a parser: what none of them matches stays plain.

export type TokenKind = 'comment' | 'string' | 'keyword' | 'number' | 'type' | 'name' | 'property'

export interface Token {
  text: string
  kind?: TokenKind
}

const number = String.raw`\b\d[\d_]*(?:\.\d+)?\b`

// Tried in this order where two could start at the same place. A pattern must not capture.
const rules: Record<Language, Array<[TokenKind, string]>> = {
  javascript: [
    ['comment', String.raw`\/\/.*`],
    ['string', String.raw`"(?:[^"\\\n]|\\.)*"|'(?:[^'\\\n]|\\.)*'|` + '`(?:[^`\\\\]|\\\\.)*`'],
    ['keyword', String.raw`\b(?:import|from|export|const|let|new|await|async|function|return|if|else|try|catch|throw|instanceof|null|true|false|undefined)\b`],
    ['number', number],
    ['type', String.raw`\b[A-Z][A-Za-z0-9]*\b`],
    ['name', String.raw`[A-Za-z_$][\w$]*(?=\()`],
    ['property', String.raw`[A-Za-z_$][\w$]*(?=:)`],
  ],
  python: [
    ['comment', String.raw`#.*`],
    ['string', String.raw`f?"(?:[^"\\\n]|\\.)*"|f?'(?:[^'\\\n]|\\.)*'`],
    ['keyword', String.raw`\b(?:import|from|as|def|return|if|elif|else|try|except|for|in|None|True|False)\b`],
    ['number', number],
    ['name', String.raw`[A-Za-z_]\w*(?=\()`],
    ['property', String.raw`[A-Za-z_]\w*(?==)`],
  ],
  // A single-quoted string runs over lines: it is the JSON body of a curl request.
  bash: [
    ['string', String.raw`"(?:[^"\\]|\\.)*"|'[^']*'`],
    ['name', String.raw`^(?:curl|npm|pnpm|yarn|bun|npx|claude)\b`],
    ['keyword', String.raw`\s-{1,2}[A-Za-z][\w-]*`],
  ],
  json: [
    ['property', String.raw`"(?:[^"\\]|\\.)*"(?=\s*:)`],
    ['string', String.raw`"(?:[^"\\]|\\.)*"`],
    ['number', String.raw`-?\b\d+(?:\.\d+)?(?:[eE][+-]?\d+)?\b`],
    ['keyword', String.raw`\b(?:true|false|null)\b`],
  ],
}

const patterns = Object.fromEntries(Object.entries(rules).map(([language, list]) => [
  language, new RegExp(list.map(([, source]) => `(${source})`).join('|'), 'gm'),
])) as Record<Language, RegExp>

// The code cut into coloured and plain pieces. Without a language it is one plain piece.
export function highlight(code: string, language?: Language): Token[] {
  if(!language) return [{ text: code }]

  const tokens: Token[] = []
  let end = 0
  for(const match of code.matchAll(patterns[language])) {
    if(match.index > end) tokens.push({ text: code.slice(end, match.index) })
    tokens.push({ text: match[0], kind: rules[language][match.slice(1).findIndex(group => group !== undefined)][0] })
    end = match.index + match[0].length
  }
  if(end < code.length) tokens.push({ text: code.slice(end) })
  return tokens
}
